import EventsAlt20 from '@carbon/icons-react/es/events--alt/20';
import {
  Col,
  Divider,
  RadioChangeEvent,
  Row,
  Space as AntdSpace,
  Typography,
} from 'antd';
import React, { FC, useEffect, useState } from 'react';
import { FormattedMessage } from 'react-intl';
import styled from 'styled-components';
import {
  CollaboratorAccessConfig,
  PlanningLevelConfig,
  SoftAllocationConfig,
} from 'api/resource-management/types';
import { Loading } from 'components/atoms/loading';
import { Radio } from 'components/atoms/radio';
import { HeaderWithIconTitle } from 'components/molecules/page/page';
import { Helium } from 'components/templates';
import { useTranslation } from 'hooks/use-translation';
import {
  useResourceManagementConfigMutation,
  useResourceManagementConfigQuery,
  useResourceManagementPlanningLevelMutation,
} from 'queries/resource-management-configuration';
import { CollaboratorAccessSettings } from './collaborator-access-settings';
import { AllocationSettingsModal } from './components/allocation-settings-modal';
import { SoftAllocationSettings } from './soft-allocation-settings';

const Space = styled(AntdSpace)`
  margin-top: var(--spacing-04);
`;
const Loader = styled(Loading)`
  padding-top: var(--spacing-04);
`;

export const getLabelForPlanningLevel = (
  planningLevel: PlanningLevelConfig,
): string => {
  switch (planningLevel) {
    case PlanningLevelConfig.HoursPerDay:
      return 'settings.resource_management.planning_level.hours_per_day';
    case PlanningLevelConfig.HoursPerWeek:
    default:
      return 'settings.resource_management.planning_level.hours_per_week';
  }
};

export const ResourceManagementSettings: FC = () => {
  const title = useTranslation('settings.resource_management.title');
  const documentTitle = useTranslation(
    'settings.resource_management.title.document',
  );

  const { data: resourceManagementConfig, isLoading } =
    useResourceManagementConfigQuery();

  const {
    mutateAsync: updateResourceManagementConfig,
    isLoading: isLoadingMutation,
  } = useResourceManagementConfigMutation();

  const {
    mutateAsync: updateResourceManagementPlanningLevel,
    isLoading: isLoadingPlanningLevelUpdate,
  } = useResourceManagementPlanningLevelMutation();

  const mutateSoftAllocations = (
    softAllocationConfig: SoftAllocationConfig,
  ) => {
    if (resourceManagementConfig) {
      updateResourceManagementConfig({
        ...resourceManagementConfig,
        softAllocations: softAllocationConfig,
      });
    }
  };

  const mutateCollaboratorAccessConfig = (
    collaboratorAccessConfiguration: CollaboratorAccessConfig,
  ) => {
    if (resourceManagementConfig) {
      updateResourceManagementConfig({
        ...resourceManagementConfig,
        accessConfig: {
          collaboratorAccessMeta: collaboratorAccessConfiguration,
        },
      });
    }
  };

  const [isModalOpen, setIsModalOpen] = useState(false);

  const mutatePlanningLevelConfig = (
    planningLevelConfiguration: PlanningLevelConfig,
  ) => {
    if (resourceManagementConfig) {
      updateResourceManagementPlanningLevel(planningLevelConfiguration).then(
        () => {
          setIsModalOpen(false);
        },
      );
    }
  };

  const [selectedValue, setSelectedValue] = useState(
    PlanningLevelConfig.HoursPerWeek,
  );

  const onConfirmPlanningLevelModal = (value: PlanningLevelConfig) => {
    setSelectedValue(value);
    mutatePlanningLevelConfig(value);
  };

  const handleRadioChange = (e: RadioChangeEvent) => {
    if (e.target.value === PlanningLevelConfig.HoursPerWeek) {
      setIsModalOpen(true);
    } else {
      setSelectedValue(e.target.value);
      if (selectedValue !== e.target.value) {
        mutatePlanningLevelConfig(e.target.value);
      }
    }
  };

  useEffect(() => {
    if (resourceManagementConfig) {
      setSelectedValue(resourceManagementConfig.planningLevelConfig);
    }
  }, [resourceManagementConfig]);

  return (
    <Helium
      title={<HeaderWithIconTitle icon={<EventsAlt20 />} title={title} />}
      documentTitle={documentTitle}
    >
      <Row>
        <Col xxl={20} sm={24}>
          <SoftAllocationSettings
            isConfigLoading={isLoading}
            initialConfig={resourceManagementConfig}
            handleConfigMutation={mutateSoftAllocations}
          />
        </Col>
        <Col xxl={20} sm={24}>
          <CollaboratorAccessSettings
            isConfigLoading={isLoading}
            initialConfig={resourceManagementConfig}
            handleCollaboratorAccessChange={mutateCollaboratorAccessConfig}
          />
        </Col>
        <AllocationSettingsModal
          show={isModalOpen}
          setShow={setIsModalOpen}
          onConfirm={onConfirmPlanningLevelModal}
          isLoading={isLoadingMutation || isLoadingPlanningLevelUpdate}
        />
      </Row>
      <Divider />
      <Row>
        <Typography.Text strong>
          <FormattedMessage id="settings.resource_management.allocation_type.title" />
        </Typography.Text>
      </Row>
      <Row>
        {isLoading ? (
          <Loader />
        ) : (
          <Radio.Group value={selectedValue} onChange={handleRadioChange}>
            <Space direction="vertical">
              {Object.values(PlanningLevelConfig).map((value) => (
                <Radio value={value}>
                  <FormattedMessage id={getLabelForPlanningLevel(value)} />
                </Radio>
              ))}
            </Space>
          </Radio.Group>
        )}
      </Row>
    </Helium>
  );
};
